'use client';

import { useState } from 'react';
import { CalendarDays, CircleAlert, CircleOff, Clock, CloudOff, type LucideProps, MapPin } from 'lucide-react';
import { getResourceRoute } from '@/app/routes';
import { Container } from '@/atoms/Container/Container';
import { Image } from '@/atoms/Image/Image';
import { Link } from '@/atoms/Link/Link';
import { Typography } from '@/atoms/Typography/Typography';
import { cn } from '@/libs/utils/utils';
import { useIsPostPreviewNavigable } from '@/molecules/PostPreviewCard/PostPreviewNestingContext';
import { PinakesStatus } from '@/services/pinakes/pinakes.types';
import { ResourceAlsoOn } from './ResourceAlsoOn';
import { type ResourceCardProps, ResourceFamily } from './ResourceCard.types';
import {
  formatResourceDates,
  formatResourcePoint,
  getDisplayLink,
  getEventStatusNote,
  getHttpUrl,
  getLinkHost,
  getResourceFamily,
  getResourceStatus,
  getResourceTypeLabel,
} from './ResourceCard.utils';
import { ResourceSimilar } from './ResourceSimilar';
import { ResourceSourceIcon } from './ResourceSourceIcon';

/** What a card that is not `ok` says about itself. Only `missing` has a documented meaning (`/llms.txt`). */
const STATUS_NOTES: Readonly<
  Record<Exclude<PinakesStatus, PinakesStatus.OK>, { text: string; icon: React.ComponentType<LucideProps> }>
> = {
  [PinakesStatus.PENDING]: { text: 'Details not read yet', icon: Clock },
  [PinakesStatus.MISSING]: { text: 'No longer available at the source', icon: CircleOff },
  [PinakesStatus.UNREACHABLE]: { text: 'The source could not be reached', icon: CloudOff },
  [PinakesStatus.ERROR]: { text: 'Details could not be read', icon: CircleAlert },
};

const DETAIL_ICON_SIZE = 14;

/**
 * One card for every external resource Pinakes knows, whatever its type: the type in plain words
 * and the source on top, then title, description and image, then the details of the type's family
 * (when an event happens, where a place is) when the card carries them, an "Open in <site>"
 * menu when the same thing is on other services too, and a "Similar" menu, read when it opens.
 * A card that is not `ok` says so and shows what it still has.
 *
 * The title opens the resource's page (`/resource?uri=<card.uri>`) and the source links out. Where
 * links may not navigate (the composer, a post quoted in a dialog) the title links out instead. The
 * `large` card heads that page: its title links out and it has no menus (the page lists them).
 *
 * Only `http(s)` values become external links or images; every text field is rendered as text.
 */
export function ResourceCard({ card, href, size = 'default' }: ResourceCardProps) {
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const navigable = useIsPostPreviewNavigable();

  const status = getResourceStatus(card.status);
  const family = getResourceFamily(card);
  const typeLabel = getResourceTypeLabel(card);
  // The post's link first (what the author wrote), then the card's own link, then the address
  // Pinakes gives for a Pubky app's object (`open_in`): never a non-web URI.
  const linkUrl = getHttpUrl(href) ?? getHttpUrl(card.uri) ?? getHttpUrl(card.open_in[0]?.url);
  // The source names the card's own site (`site`), so its favicon comes from the card's own link
  // when that is web, not from an alias the post may use (a short link, a mobile host).
  const sourceUri = getHttpUrl(card.uri) ?? linkUrl ?? card.uri;
  const siteName = card.site || getLinkHost(sourceUri);
  const title = card.name || getDisplayLink(href || card.uri);
  const imageUrl = getHttpUrl(card.image);
  const showImage = imageUrl !== null && failedImage !== imageUrl;

  const dates = family === ResourceFamily.EVENT ? formatResourceDates(card) : null;
  const venue = family === ResourceFamily.EVENT ? card.venue?.name?.trim() || null : null;
  // An event's venue by name when the card has one, its point otherwise.
  const point = family !== ResourceFamily.WEB && !venue && card.point ? formatResourcePoint(card.point) : null;
  const eventStatusNote = family === ResourceFamily.EVENT ? getEventStatusNote(card.event_status) : null;
  const statusNote = status === PinakesStatus.OK ? null : STATUS_NOTES[status];
  const isLarge = size === 'large';
  // A `missing` card's own link is gone upstream: the page's heading and source do not send the
  // viewer there.
  const titleUrl = status === PinakesStatus.MISSING ? null : linkUrl;
  const smallTitleUrl = navigable ? getResourceRoute(card.uri) : linkUrl;

  return (
    <Container
      as="article"
      data-testid="resource-card"
      data-status={status}
      data-family={family}
      data-size={size}
      className="gap-3 rounded-md bg-muted p-4 sm:p-6"
    >
      <Container className="flex-row items-center justify-between gap-3">
        {typeLabel && (
          <Typography as="span" size="xs" data-testid="resource-card-type" className="truncate text-muted-foreground">
            {typeLabel}
          </Typography>
        )}
        <ResourceSource uri={sourceUri} href={isLarge ? titleUrl : linkUrl} name={siteName} />
      </Container>

      <Container className="flex-row gap-4">
        <Container className="min-w-0 flex-1 gap-1.5">
          {isLarge ? (
            <Typography
              as="h1"
              overrideDefaults
              className="text-xl font-bold wrap-anywhere text-foreground sm:text-2xl"
            >
              {titleUrl ? (
                <Link href={titleUrl} overrideDefaults data-testid="resource-card-title" className="hover:text-brand">
                  {title}
                </Link>
              ) : (
                <span data-testid="resource-card-title">{title}</span>
              )}
            </Typography>
          ) : smallTitleUrl ? (
            <Link
              href={smallTitleUrl}
              overrideDefaults
              data-testid="resource-card-title"
              className="text-base font-semibold wrap-anywhere text-foreground hover:text-brand"
            >
              {title}
            </Link>
          ) : (
            <Typography data-testid="resource-card-title" className="font-semibold wrap-anywhere">
              {title}
            </Typography>
          )}

          {card.description && (
            <Typography
              size="sm"
              data-testid="resource-card-description"
              className={cn('font-normal wrap-anywhere text-muted-foreground', !isLarge && 'line-clamp-2')}
            >
              {card.description}
            </Typography>
          )}

          {dates && <ResourceDetail icon={CalendarDays} text={dates} testId="resource-card-dates" />}
          {venue && <ResourceDetail icon={MapPin} text={venue} testId="resource-card-venue" />}
          {point && <ResourceDetail icon={MapPin} text={point} testId="resource-card-point" />}
          {eventStatusNote && (
            <ResourceDetail icon={CircleAlert} text={eventStatusNote} testId="resource-card-event-status" />
          )}
          {card.attribution && (
            <Typography
              size="xs"
              data-testid="resource-card-attribution"
              className="font-normal wrap-anywhere text-muted-foreground"
            >
              {card.attribution}
            </Typography>
          )}
          {statusNote && <ResourceDetail icon={statusNote.icon} text={statusNote.text} testId="resource-card-status" />}
        </Container>

        {showImage && (
          <Image
            src={imageUrl}
            alt=""
            width={isLarge ? 160 : 96}
            height={isLarge ? 160 : 96}
            data-testid="resource-card-image"
            onError={() => setFailedImage(imageUrl)}
            className={cn(
              'shrink-0 rounded-md object-cover object-center',
              isLarge ? 'size-24 sm:size-40' : 'size-20 sm:size-24',
            )}
          />
        )}
      </Container>

      {!isLarge && (
        <Container className="flex-row flex-wrap items-center gap-2">
          {/* A `missing` card's own link is gone upstream: the menu offers only the other services. */}
          <ResourceAlsoOn
            alsoOn={card.also_on}
            href={status === PinakesStatus.MISSING ? null : linkUrl}
            uri={sourceUri}
            site={siteName}
          />
          <ResourceSimilar id={card.id} title={title} />
        </Container>
      )}
    </Container>
  );
}

/** The site the card's link goes to, top right: favicon and name, linking out when the link is web. */
function ResourceSource({ uri, href, name }: { uri: string; href: string | null; name: string | null }) {
  const content = (
    <>
      <ResourceSourceIcon uri={uri} />
      {name && <span className="truncate">{name}</span>}
    </>
  );
  const className = 'ml-auto flex min-w-0 shrink items-center gap-1.5 text-xs font-medium text-muted-foreground';

  // Without a name the icon alone would be an unlabelled link: the title already links out.
  if (!href || !name) {
    return (
      <span data-testid="resource-card-source" className={className}>
        {content}
      </span>
    );
  }
  return (
    <Link href={href} overrideDefaults data-testid="resource-card-source" className={cn(className, 'hover:text-brand')}>
      {content}
    </Link>
  );
}

function ResourceDetail({
  icon: Icon,
  text,
  testId,
}: {
  icon: React.ComponentType<LucideProps>;
  text: string;
  testId: string;
}) {
  return (
    <Container data-testid={testId} className="flex-row items-center gap-1.5 text-muted-foreground">
      <Icon size={DETAIL_ICON_SIZE} aria-hidden="true" className="shrink-0" />
      <Typography as="span" size="sm" className="font-normal wrap-anywhere text-muted-foreground">
        {text}
      </Typography>
    </Container>
  );
}
